import { useState, useEffect } from "react";
import {
  Briefcase, Users, FileText, Settings,
  LayoutDashboard, Bell, Smartphone, Wallet, Package,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminLiveFeed } from "@/components/admin/adminApi";
import StatsTab from "@/components/admin/StatsTab";
import OffersTab from "@/components/admin/OffersTab";
import ApplicationsTab from "@/components/admin/ApplicationsTab";
import RemindersTab from "@/components/admin/RemindersTab";
import SMSTab from "@/components/admin/SMSTab";
import CandidatesTab from "@/components/admin/CandidatesTab";
import ConfigurationTab from "@/components/admin/ConfigurationTab";
import AcompteTab from "@/components/admin/AcompteTab";
import OrdersTab from "@/components/admin/OrdersTab";

const TRIGGER_CLS = "rounded-none data-[state=active]:bg-[#662483] data-[state=active]:text-white font-semibold gap-2";

export default function AdminDashboard() {
  const liveEvents = useAdminLiveFeed();
  const [activeTab, setActiveTab] = useState("stats");
  const [candidatesInitialFilter, setCandidatesInitialFilter] = useState(null);
  // Optional filter pushed to ApplicationsTab when admin clicks the application
  // count badge on an offer card (or anywhere else later).
  const [applicationsInitialFilter, setApplicationsInitialFilter] = useState(null);

  const navigateToCandidates = (filters) => {
    setCandidatesInitialFilter({ filters, version: Date.now() });
    setActiveTab("candidates");
  };
  // Open the candidate profile directly in the Candidates tab.
  const openCandidate = (userId) => {
    setCandidatesInitialFilter({ filters: { open_user_id: userId }, version: Date.now() });
    setActiveTab("candidates");
  };
  const navigateToOffers = () => setActiveTab("offers");
  const navigateToApplications = (filters) => {
    if (filters) setApplicationsInitialFilter({ filters, version: Date.now() });
    setActiveTab("applications");
  };

  // Deep-link : /admin?tab=candidates&user_id=... provenant p.ex. de l'email
  // de notification d'inscription — ouvre directement la fiche candidat.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab");
      const uid = params.get("user_id");
      if (tab === "candidates" && uid) {
        openCandidate(uid);
      } else if (tab) {
        setActiveTab(tab);
      }
    } catch { /* noop */ }
  }, []);

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#3F3F3F]">Backoffice</h1>
            <p className="text-sm text-gray-500 mt-1">Gestion FR INTERIM</p>
          </div>
          {liveEvents.length > 0 && (
            <div className="bg-white border border-gray-200 px-4 py-2 max-w-md w-full" data-testid="admin-live-feed">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /> Activité temps réel
              </p>
              <ul className="space-y-1 max-h-20 overflow-y-auto">
                {liveEvents.slice(0, 3).map((e) => (
                  <li key={e._id} className="text-xs text-gray-700 truncate">
                    <span className="font-semibold text-[#662483]">{e.title}</span> — {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 rounded-none p-1 gap-1 flex-wrap h-auto">
            <TabsTrigger value="stats" className={TRIGGER_CLS} data-testid="tab-stats"><LayoutDashboard className="w-4 h-4" /> Tableau de bord</TabsTrigger>
            <TabsTrigger value="offers" className={TRIGGER_CLS} data-testid="tab-offers"><Briefcase className="w-4 h-4" /> Offres</TabsTrigger>
            <TabsTrigger value="orders" className={TRIGGER_CLS} data-testid="tab-orders"><Package className="w-4 h-4" /> Commandes</TabsTrigger>
            <TabsTrigger value="applications" className={TRIGGER_CLS} data-testid="tab-applications"><FileText className="w-4 h-4" /> Candidatures</TabsTrigger>
            <TabsTrigger value="candidates" className={TRIGGER_CLS} data-testid="tab-candidates"><Users className="w-4 h-4" /> Vivier</TabsTrigger>
            <TabsTrigger value="reminders" className={TRIGGER_CLS} data-testid="tab-reminders"><Bell className="w-4 h-4" /> Rappels CRM</TabsTrigger>
            <TabsTrigger value="sms" className={TRIGGER_CLS} data-testid="tab-sms"><Smartphone className="w-4 h-4" /> SMS</TabsTrigger>
            <TabsTrigger value="acompte" className={TRIGGER_CLS} data-testid="tab-acompte"><Wallet className="w-4 h-4" /> Acomptes</TabsTrigger>
            <TabsTrigger value="configuration" className={TRIGGER_CLS} data-testid="tab-configuration"><Settings className="w-4 h-4" /> Configuration</TabsTrigger>
          </TabsList>
          <TabsContent value="stats">
            <StatsTab
              onNavigateToCandidates={navigateToCandidates}
              onNavigateToOffers={navigateToOffers}
              onNavigateToApplications={navigateToApplications}
            />
          </TabsContent>
          <TabsContent value="offers"><OffersTab onNavigateToApplications={navigateToApplications} /></TabsContent>
          <TabsContent value="orders"><OrdersTab onNavigateToCandidates={navigateToCandidates} /></TabsContent>
          <TabsContent value="applications"><ApplicationsTab initialFilter={applicationsInitialFilter} onOpenCandidate={openCandidate} /></TabsContent>
          <TabsContent value="candidates"><CandidatesTab initialFilter={candidatesInitialFilter} /></TabsContent>
          <TabsContent value="reminders"><RemindersTab onNavigateToCandidates={navigateToCandidates} /></TabsContent>
          <TabsContent value="sms"><SMSTab /></TabsContent>
          <TabsContent value="acompte"><AcompteTab /></TabsContent>
          <TabsContent value="configuration"><ConfigurationTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
